import React, { useEffect, useRef, useState } from "react";

import { MESSAGE_TOKENS, previewTokens } from "../catalog";
import { keyActivate } from "../useOutsideClick";
import { insertTokenInto } from "./helpers";
import { IconChevron, IconCheck, IconClose, IconPlus } from "./icons";

// Options can carry an optional `group` (rendered as a section header, e.g.
// "Active" / "Inactive") — groups render in first-seen order, each option
// still keeps its own position within its group.
export function SelectControl({ value, options, openKey, openSelect, setOpenSelect, onChange, searchable = false }) {
  const [q, setQ] = useState("");
  const searchRef = useRef(null);
  const normalized = (options || []).map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt
  );
  const current = normalized.find((o) => o.value === value) || normalized[0];
  const open = openSelect === openKey;
  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);
  const needle = searchable ? q.trim().toLowerCase() : "";
  const visible = needle
    ? normalized.filter((o) => o.value && o.label.toLowerCase().includes(needle))
    : normalized;
  const groups = [];
  visible.forEach((opt) => {
    const name = opt.group || null;
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { name, items: [] };
      groups.push(g);
    }
    g.items.push(opt);
  });
  const pick = (v) => {
    onChange(v);
    setOpenSelect(null);
  };
  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        className={`gmba-selin${open ? " gmba-open" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          setQ("");
          setOpenSelect(open ? null : openKey);
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {current?.label || "Select…"}
        </span>
        <span className="gmba-cv"><IconChevron /></span>
      </button>
      {open && (
        <div
          className="gmba-selmenu"
          onClick={(e) => e.stopPropagation()}
          style={searchable ? { padding: 0 } : undefined}
        >
          {searchable && (
            <div className="gmba-ddsearch">
              <input
                ref={searchRef}
                placeholder="Search people…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          )}
          <div style={searchable ? { maxHeight: 240, overflowY: "auto", padding: 5 } : undefined}>
          {!visible.length && (
            <div className="gmba-selopt" style={{ cursor: "default" }}>No matches</div>
          )}
          {groups.map((g) => (
            <React.Fragment key={g.name || "_"}>
              {g.name && <div className="gmba-selgrp">{g.name}</div>}
              {g.items.map((opt) => (
                <div
                  key={opt.value}
                  className={`gmba-selopt${opt.value === value ? " gmba-selon" : ""}${opt.isActive === false ? " gmba-inactive" : ""}`}
                  onClick={() => pick(opt.value)}
                  {...keyActivate(() => pick(opt.value))}
                >
                  <span>{opt.label}</span>
                  {opt.isActive === false && !opt.group && <span className="gmba-inactivetag">Inactive</span>}
                  {opt.value === value && (
                    <span style={{ lineHeight: 0, flex: "0 0 auto" }}><IconCheck /></span>
                  )}
                </div>
              ))}
            </React.Fragment>
          ))}
          </div>
        </div>
      )}
    </div>
  );
}

// "Also notify" — an optional add-on list of specific people, layered on top
// of a notify action's built-in target (the player/manager/workspace/except
// crowd it already reaches). Never replaces that built-in recipient.
export function PeopleMultiSelect({ value, options, openKey, openSelect, setOpenSelect, onChange }) {
  const [q, setQ] = useState("");
  const selectedIds = Array.isArray(value) ? value : [];
  const open = openSelect === openKey;
  const available = options.filter((o) => !selectedIds.includes(o.value));
  const needle = q.trim().toLowerCase();
  const visible = needle
    ? available.filter((o) => o.label.toLowerCase().includes(needle))
    : available;
  const groups = [];
  visible.forEach((opt) => {
    const name = opt.group || null;
    let g = groups.find((x) => x.name === name);
    if (!g) {
      g = { name, items: [] };
      groups.push(g);
    }
    g.items.push(opt);
  });

  const add = (id) => onChange([...selectedIds, id]);
  const remove = (id) => onChange(selectedIds.filter((v) => v !== id));

  return (
    <div>
      {selectedIds.length > 0 && (
        <div className="gmba-chips" style={{ marginBottom: 8 }}>
          {selectedIds.map((id) => {
            const opt = options.find((o) => o.value === id);
            return (
              <span key={id} className="gmba-chipfix">
                {opt?.label || "Removed from workspace"}
                {opt?.isActive === false && " (inactive)"}
                <button
                  type="button"
                  aria-label={`Remove ${opt?.label || "person"}`}
                  className="gmba-chipx"
                  onClick={() => remove(id)}
                >
                  <IconClose size={12} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <button
          type="button"
          className={`gmba-selin${open ? " gmba-open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setQ("");
            setOpenSelect(open ? null : openKey);
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <IconPlus size={13} /> Also notify…
          </span>
          <span className="gmba-cv"><IconChevron /></span>
        </button>
        {open && (
          <div className="gmba-selmenu" onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
            <div className="gmba-ddsearch">
              <input
                placeholder="Search people…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: 200, overflowY: "auto", padding: 5 }}>
              {!visible.length && (
                <div className="gmba-selopt" style={{ cursor: "default" }}>
                  {available.length ? "No matches" : "Everyone's already added"}
                </div>
              )}
              {groups.map((g) => (
                <React.Fragment key={g.name || "_"}>
                  {g.name && <div className="gmba-selgrp">{g.name}</div>}
                  {g.items.map((opt) => {
                    const pick = () => {
                      add(opt.value);
                      setQ("");
                    };
                    return (
                      <div
                        key={opt.value}
                        className={`gmba-selopt${opt.isActive === false ? " gmba-inactive" : ""}`}
                        onClick={pick}
                        {...keyActivate(pick)}
                      >
                        <span>{opt.label}</span>
                        {opt.isActive === false && !opt.group && <span className="gmba-inactivetag">Inactive</span>}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ChannelChips({ value, options, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (c) => {
    if (selected.includes(c)) {
      // keep at least one channel — an alert nobody receives isn't an alert
      if (selected.length > 1) onChange(selected.filter((x) => x !== c));
    } else {
      onChange([...selected, c]);
    }
  };
  return (
    <div className="gmba-chips">
      {options.map((c) => {
        const on = selected.includes(c);
        return (
          <button
            key={c}
            type="button"
            className={`gmba-chan${on ? " gmba-chanon" : ""}`}
            aria-pressed={on}
            onClick={() => toggle(c)}
          >
            {on && <IconCheck size={12} />}
            {c}
          </button>
        );
      })}
    </div>
  );
}

// One token button per available token. What each one means shows in the
// hint line below on hover/focus (not a floating tooltip — a tooltip
// centered on a button near the panel's edge gets clipped by the scrolling
// container, since it has nowhere to overflow into).
function TokenRow({ onInsert, defaultHint }) {
  const [hoveredHint, setHoveredHint] = useState(null);
  const clearTimer = useRef(null);

  // Setting a hint is immediate; clearing it is debounced. A brief in/out
  // blip right at any boundary (the gap between two pills, the edge shared
  // with the textarea above) then gets absorbed by the pending timeout
  // instead of visibly flickering the text — genuinely moving to a new pill
  // or away from the row still updates promptly since setHint cancels the
  // pending clear.
  const setHint = (hint) => {
    if (clearTimer.current) {
      clearTimeout(clearTimer.current);
      clearTimer.current = null;
    }
    setHoveredHint(hint);
  };
  const scheduleClear = () => {
    clearTimer.current = setTimeout(() => setHoveredHint(null), 80);
  };
  useEffect(() => () => {
    if (clearTimer.current) clearTimeout(clearTimer.current);
  }, []);

  const onRowMouseOver = (e) => {
    const hint = e.target.closest?.('.gmba-tok')?.dataset.hint;
    if (hint) setHint(hint);
  };

  return (
    <div>
      {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events -- the
          equivalent keyboard access (onFocus/onBlur) is on each button below;
          this row-level onMouseOver only exists to avoid flicker between
          adjacent pills, it doesn't add new functionality. */}
      <div className="gmba-tokrow" onMouseOver={onRowMouseOver} onMouseLeave={scheduleClear}>
        {MESSAGE_TOKENS.map((t) => (
          <button
            key={t.key}
            type="button"
            className="gmba-tok"
            data-hint={t.hint}
            onClick={() => onInsert(t.key)}
            onFocus={() => setHint(t.hint)}
            onBlur={scheduleClear}
            aria-label={`Insert ${t.key} — ${t.hint}`}
          >
            + {t.key}
          </button>
        ))}
      </div>
      {(hoveredHint || defaultHint) && (
        <div className="gmba-hint gmba-tokhint">{hoveredHint || defaultHint}</div>
      )}
    </div>
  );
}

const SMS_SOFT_LIMIT = 140;

// `channel` drives the SMS character count; `previewSubject`, when passed
// (only by the Email tab), makes the preview show the subject line above the
// body so "what this looks like" reads as one real message, not two halves.
function MessageEditor({ value, placeholder, onChange, channel, workspaceName, previewSubject }) {
  const ref = useRef(null);
  const [showPreview, setShowPreview] = useState(false);
  const insertToken = (token) => insertTokenInto(ref, value, onChange, token);
  const len = (value || "").length;
  const isSms = channel === "SMS";

  return (
    <div>
      <div className="gmba-msg">
        <textarea
          ref={ref}
          value={value || ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      <TokenRow
        onInsert={insertToken}
        defaultHint={<>Tokens like {"{First name}"} fill in with each recipient's real info when it sends.</>}
      />
      {isSms && (
        <div className={`gmba-charcount${len > SMS_SOFT_LIMIT ? " gmba-charwarn" : ""}`}>
          {len} character{len === 1 ? "" : "s"}
          {len > SMS_SOFT_LIMIT ? " — on the long side for one text" : ""}. A scorecard link is added automatically when it sends.
        </div>
      )}
      <div className="gmba-preview">
        <button
          type="button"
          className="gmba-previewtoggle"
          aria-expanded={showPreview}
          onClick={() => setShowPreview((s) => !s)}
        >
          {showPreview ? "Hide preview" : "Show preview"}
          <span className={`gmba-cv${showPreview ? " gmba-cvup" : ""}`} style={{ lineHeight: 0 }}>
            <IconChevron size={13} />
          </span>
        </button>
        {showPreview && (
          <div className="gmba-previewbox">
            <div className="gmba-previewlbl">What this looks like</div>
            {previewSubject !== undefined && (
              <div className="gmba-previewsubject">{previewTokens(previewSubject, workspaceName) || "(no subject)"}</div>
            )}
            <div className="gmba-previewtext">{previewTokens(value, workspaceName) || "(empty message)"}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Per-channel messaging. One channel keeps the simple editor (email gains a
   subject line); several channels split into tabs so SMS stays short while
   email gets its own subject + body. Stored as config.messages =
   { sms, inapp, email: { subject, body } }. The legacy single config.message
   stays the fallback for any channel without its own version, so automations
   saved before this feature keep sending exactly what they did. */
export function ChannelMessages({ channels, message, messages, placeholder, onMessage, onMessages, workspaceName }) {
  const chans = Array.isArray(channels) && channels.length ? channels : ["SMS"];
  const [tab, setTab] = useState(chans[0]);
  const active = chans.includes(tab) ? tab : chans[0];
  const m = messages || {};
  const email = m.email || {};
  const subjectRef = useRef(null);
  const setPart = (key, val) => onMessages({ ...m, [key]: val });
  const setSubject = (v) => setPart("email", { ...email, subject: v });

  const editorFor = (chan) => {
    if (chan === "Email") {
      return (
        <div>
          <input
            ref={subjectRef}
            className="gmba-textin"
            style={{ marginBottom: 4 }}
            value={email.subject || ""}
            placeholder="Email subject — e.g. Happy birthday {First name}!"
            onChange={(e) => setSubject(e.target.value)}
          />
          <TokenRow
            onInsert={(token) => insertTokenInto(subjectRef, email.subject, setSubject, token)}
            defaultHint="Subject tokens above insert into the subject line; the body has its own set below."
          />
          <MessageEditor
            value={email.body ?? message}
            placeholder="Write the email body…"
            onChange={(v) => setPart("email", { ...email, body: v })}
            channel="Email"
            workspaceName={workspaceName}
            previewSubject={email.subject || ""}
          />
        </div>
      );
    }
    if (chan === "SMS") {
      return (
        <MessageEditor
          value={m.sms ?? message}
          placeholder={placeholder}
          onChange={(v) => setPart("sms", v)}
          channel="SMS"
          workspaceName={workspaceName}
        />
      );
    }
    return (
      <MessageEditor
        value={m.inapp ?? message}
        placeholder={placeholder}
        onChange={(v) => setPart("inapp", v)}
        channel="In-app"
        workspaceName={workspaceName}
      />
    );
  };

  if (chans.length === 1) {
    if (chans[0] === "Email") return editorFor("Email");
    return (
      <MessageEditor
        value={message}
        placeholder={placeholder}
        onChange={onMessage}
        channel={chans[0]}
        workspaceName={workspaceName}
      />
    );
  }
  return (
    <div>
      <div className="gmba-msgtabs">
        {chans.map((c) => (
          <button
            key={c}
            type="button"
            className={`gmba-msgtab${active === c ? " gmba-msgtabon" : ""}`}
            onClick={() => setTab(c)}
          >
            {c}
          </button>
        ))}
      </div>
      {editorFor(active)}
    </div>
  );
}
